/**
 * Presenter for the chronology version-compare panel. Renders the diff result produced by
 * `diffChronology.ts` as a readable comparison, one section at a time: the `section` prop
 * selects whether the case-summary card or the list of visit cards is shown, so this same
 * component can be embedded inside either the Summary or Visits chronology tab. The sticky
 * toolbar (version names and close button) always renders regardless of section. Added/removed
 * visits render their full content snapshot (not just a title) so a reviewer can see what
 * actually changed.
 */
import { getCitationKey } from "@/components/MedicalChronology/Visits/VisitCitationList";
import type { MedChronTemplate } from "@/components/MedicalChronology/Visits/types";
import {
  formatCitationLabel,
  formatVisitType,
} from "@/components/MedicalChronology/Visits/utils/visitFormatting";
import { Badge } from "@/components/ui/Badge";
import { LabeledSwitch } from "@/components/ui/LabeledSwitch";
import type { MedVisitCitation } from "@/services/medChronServiceV2.types";
import type { MedVisit, MedVisitDetailResponse } from "@/types/medchron";
import { AlertCircle, ArrowRight, ChevronDown, GitCompare, X } from "lucide-react";
import React, { useState } from "react";

import { COMPARE_VERSIONS_SECTIONS, VisitDiffStatus, VisitMatchMode } from "./types";
import type {
  DetailCategoryDiff,
  FieldDiff,
  SummaryFieldDiff,
  TextDiffSegment,
  VersionsCompareViewProps,
  VisitDiffEntry,
} from "./types";

const VISIT_FIELD_LABELS: Record<string, string> = {
  visit_date: "Date",
  visit_type_id: "Visit Type",
  provider_name: "Provider",
  facility_name: "Facility",
  visit_subtype: "Subtype",
};

const SUMMARY_FIELD_LABELS: Record<string, string> = {
  patient_name: "Patient Name",
  date_of_birth: "Date of Birth",
  date_of_injury: "Date of Injury",
  accident_description: "Accident Description",
  key_injuries: "Key Injuries",
  key_test_results: "Key Test Results",
  key_diagnoses: "Key Diagnoses",
};

const STATUS_BADGE_CLASSES: Record<VisitDiffEntry["status"], string> = {
  added: "bg-diff-added-bg text-diff-added-text ring-diff-added-border/80",
  removed: "bg-diff-removed-bg text-diff-removed-text ring-diff-removed-border/80",
  modified: "bg-diff-modified-bg text-diff-modified-text ring-diff-modified-border/80",
  unchanged: "bg-neutral-100 text-neutral-500 ring-neutral-200/80",
};

const CARD_BORDER_CLASSES: Record<VisitDiffEntry["status"], string> = {
  added: "border-diff-added-border/80",
  removed: "border-diff-removed-border/80",
  modified: "border-neutral-200",
  unchanged: "border-neutral-200",
};

const TALLY_LABELS: Record<VisitDiffStatus, string> = {
  [VisitDiffStatus.Added]: "added",
  [VisitDiffStatus.Removed]: "removed",
  [VisitDiffStatus.Modified]: "modified",
  [VisitDiffStatus.Unchanged]: "unchanged",
};

const TALLY_CLASSES: Record<VisitDiffStatus, string> = {
  [VisitDiffStatus.Added]: "text-diff-added-text",
  [VisitDiffStatus.Removed]: "text-diff-removed-text",
  [VisitDiffStatus.Modified]: "text-diff-modified-text",
  [VisitDiffStatus.Unchanged]: "",
};

const MATCH_MODE_LABELS: Record<VisitMatchMode, string> = {
  [VisitMatchMode.Id]: "Matched by visit ID",
  [VisitMatchMode.Content]: "Matched by content",
};

type TintTone = "removed" | "added";

const TONE_PANEL_CLASSES: Record<TintTone, string> = {
  removed: "bg-diff-removed-bg/60 ring-diff-removed-border/60",
  added: "bg-diff-added-bg/60 ring-diff-added-border/60",
};

const TONE_LABEL_CLASSES: Record<TintTone, string> = {
  removed: "text-diff-removed-text",
  added: "text-diff-added-text",
};

const CHIP_CLASSES: Record<TintTone, string> = {
  removed: "bg-diff-removed-bg text-diff-removed-text ring-diff-removed-border/80",
  added: "bg-diff-added-bg text-diff-added-text ring-diff-added-border/80",
};

const formatFieldValue = (
  field: string,
  value: unknown,
  template: MedChronTemplate | null
): string => {
  if (field === "visit_type_id") return formatVisitType(template, value as string);
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "—";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const StatusBadge: React.FC<{ status: VisitDiffEntry["status"] }> = ({ status }) => (
  <span
    className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-micro ring-1 ring-inset ${STATUS_BADGE_CLASSES[status]}`}
  >
    {status}
  </span>
);

const Chip: React.FC<{ tone: TintTone; label: string }> = ({ tone, label }) => (
  <span
    className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-micro ring-1 ring-inset ${CHIP_CLASSES[tone]}`}
  >
    {label}
  </span>
);

const TextBlockPanel: React.FC<{ tone: TintTone; text: string }> = ({ tone, text }) => (
  <div
    className={`rounded-lg p-2.5 ring-1 ${TONE_PANEL_CLASSES[tone]} whitespace-pre-wrap text-sm leading-relaxed text-neutral-800`}
  >
    {text}
  </div>
);

const TextSegments: React.FC<{ segments: TextDiffSegment[] }> = ({ segments }) => (
  <>
    {segments.map((segment, index) => {
      if (segment.added) {
        return (
          <ins
            key={index}
            className="rounded-sm bg-diff-added-strong px-0.5 text-diff-added-text no-underline"
          >
            {segment.value}
          </ins>
        );
      }
      if (segment.removed) {
        return (
          <del
            key={index}
            className="rounded-sm bg-diff-removed-strong px-0.5 text-diff-removed-text"
          >
            {segment.value}
          </del>
        );
      }
      return <span key={index}>{segment.value}</span>;
    })}
  </>
);

const FieldDiffRow: React.FC<{ diff: FieldDiff; template: MedChronTemplate | null }> = ({
  diff,
  template,
}) => {
  if (!diff.changed) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm">
      <span className="text-2xs font-semibold uppercase tracking-micro text-neutral-400">
        {VISIT_FIELD_LABELS[diff.field] ?? diff.field}
      </span>
      <span className="text-diff-removed-text line-through">
        {formatFieldValue(diff.field, diff.oldValue, template)}
      </span>
      <ArrowRight size={12} className="shrink-0 text-neutral-300" aria-hidden />
      <span className="font-medium text-diff-added-text">
        {formatFieldValue(diff.field, diff.newValue, template)}
      </span>
    </div>
  );
};

const CitationLines: React.FC<{
  citations: MedVisitCitation[];
  fileMapping: Record<string, string>;
  className?: string;
}> = ({ citations, fileMapping, className = "" }) =>
  citations.length === 0 ? (
    <span className={className}>No citations</span>
  ) : (
    <ul className={className}>
      {citations.map((citation, index) => (
        <li key={getCitationKey(citation, index)}>{formatCitationLabel(citation, fileMapping)}</li>
      ))}
    </ul>
  );

const CitationsDiffRow: React.FC<{
  oldCitations: MedVisitCitation[];
  newCitations: MedVisitCitation[];
  fileMapping: Record<string, string>;
}> = ({ oldCitations, newCitations, fileMapping }) => (
  <div className="flex flex-wrap items-start gap-x-1.5 gap-y-0.5 text-sm">
    <span className="text-2xs font-semibold uppercase tracking-micro text-neutral-400">
      Citations
    </span>
    <CitationLines
      citations={oldCitations}
      fileMapping={fileMapping}
      className="text-diff-removed-text line-through"
    />
    <ArrowRight size={12} className="mt-1 shrink-0 text-neutral-300" aria-hidden />
    <CitationLines
      citations={newCitations}
      fileMapping={fileMapping}
      className="font-medium text-diff-added-text"
    />
  </div>
);

const DetailDiffBlock: React.FC<{ diff: DetailCategoryDiff }> = ({ diff }) => (
  <div className="rounded-lg bg-neutral-50/80 p-2.5 text-sm ring-1 ring-neutral-100">
    {diff.previousCategory ? (
      <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-2xs font-semibold uppercase tracking-micro">
        <span className="text-diff-removed-text line-through">{diff.previousCategory}</span>
        <ArrowRight size={10} className="shrink-0 text-neutral-300" aria-hidden />
        <span className="text-diff-added-text">{diff.category}</span>
      </div>
    ) : (
      <div className="text-2xs font-semibold uppercase tracking-micro text-neutral-400">
        {diff.category}
      </div>
    )}
    <div className="mt-1 whitespace-pre-wrap leading-relaxed text-neutral-700">
      <TextSegments segments={diff.segments} />
    </div>
  </div>
);

const DetailToneBlock: React.FC<{
  tone: TintTone;
  category: string;
  text: string;
  chipLabel: string;
}> = ({ tone, category, text, chipLabel }) => (
  <div className={`rounded-lg p-2.5 ring-1 ${TONE_PANEL_CLASSES[tone]}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span
        className={`text-2xs font-semibold uppercase tracking-micro ${TONE_LABEL_CLASSES[tone]}`}
      >
        {category}
      </span>
      <Chip tone={tone} label={chipLabel} />
    </div>
    <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">{text}</div>
  </div>
);

const VisitCardHeader: React.FC<{
  visit: MedVisit;
  status: VisitDiffEntry["status"];
  template: MedChronTemplate | null;
  fileMapping: Record<string, string>;
  detailCount?: number;
}> = ({ visit, status, template, fileMapping, detailCount }) => (
  <div>
    <div className="flex items-start justify-between gap-3">
      <span className="truncate font-semibold text-neutral-900">
        {visit.visit_date ?? "Unknown date"} — {formatVisitType(template, visit.visit_type_id)}
      </span>
      <StatusBadge status={status} />
    </div>
    <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-neutral-500">
      <span className="font-medium text-neutral-600">
        {visit.provider_name ?? "Unknown provider"}
      </span>
      {visit.facility_name && (
        <>
          <span aria-hidden>·</span>
          <span>{visit.facility_name}</span>
        </>
      )}
      {visit.visit_subtype && (
        <>
          <span aria-hidden>·</span>
          <span>{visit.visit_subtype}</span>
        </>
      )}
      {detailCount !== undefined && (
        <>
          <span aria-hidden>·</span>
          <span>
            {detailCount} {detailCount === 1 ? "detail" : "details"}
          </span>
        </>
      )}
    </div>
    <CitationLines
      citations={visit.citations}
      fileMapping={fileMapping}
      className="mt-1 text-xs text-neutral-500"
    />
  </div>
);

const VisitSnapshotDetails: React.FC<{ details: MedVisitDetailResponse[]; tone: TintTone }> = ({
  details,
  tone,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const sortedDetails = [...details].sort((a, b) => a.order_id - b.order_id);

  return (
    <div className="mt-3 border-t border-neutral-100 pt-3">
      <button
        type="button"
        aria-expanded={!isCollapsed}
        onClick={() => setIsCollapsed((current) => !current)}
        className="flex w-full items-center justify-between gap-2 rounded-lg text-xs font-semibold text-neutral-500 transition-colors duration-200 hover:text-neutral-700 focus-visible:shadow-focus"
      >
        <span>Visit Details ({sortedDetails.length})</span>
        <ChevronDown
          size={14}
          aria-hidden
          className={`shrink-0 transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`}
        />
      </button>
      {!isCollapsed && (
        <div className="mt-2 space-y-2">
          {sortedDetails.length === 0 ? (
            <div className="text-xs text-neutral-400">No visit details</div>
          ) : (
            sortedDetails.map((detail) => (
              <div
                key={`${detail.visit_detail_category}-${detail.order_id}`}
                className={`rounded-lg p-2.5 ring-1 ${TONE_PANEL_CLASSES[tone]}`}
              >
                <div
                  className={`text-2xs font-semibold uppercase tracking-micro ${TONE_LABEL_CLASSES[tone]}`}
                >
                  {detail.visit_detail_category}
                </div>
                <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
                  {detail.visit_details}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const VisitDiffCard: React.FC<{
  entry: VisitDiffEntry;
  template: MedChronTemplate | null;
  fileMapping: Record<string, string>;
}> = ({ entry, template, fileMapping }) => {
  const visit = entry.newVisit ?? entry.oldVisit;
  if (!visit) return null;

  return (
    <div className={`rounded-xl border bg-card p-4 shadow-sm ${CARD_BORDER_CLASSES[entry.status]}`}>
      <VisitCardHeader
        visit={visit}
        status={entry.status}
        template={template}
        fileMapping={fileMapping}
        detailCount={
          entry.status === VisitDiffStatus.Added || entry.status === VisitDiffStatus.Removed
            ? visit.med_visit_details.length
            : undefined
        }
      />
      {entry.status === VisitDiffStatus.Removed && (
        <VisitSnapshotDetails details={visit.med_visit_details} tone="removed" />
      )}
      {entry.status === VisitDiffStatus.Added && (
        <VisitSnapshotDetails details={visit.med_visit_details} tone="added" />
      )}
      {entry.status === VisitDiffStatus.Modified && (
        <div className="mt-3 space-y-2 border-t border-neutral-100 pt-3">
          {entry.fieldDiffs.map((fieldDiff) => (
            <FieldDiffRow key={fieldDiff.field} diff={fieldDiff} template={template} />
          ))}
          {entry.citationsChanged && entry.oldVisit && entry.newVisit && (
            <CitationsDiffRow
              oldCitations={entry.oldVisit.citations}
              newCitations={entry.newVisit.citations}
              fileMapping={fileMapping}
            />
          )}
          {entry.detailDiffs
            .filter((detailDiff) => detailDiff.changed)
            .map((detailDiff, index) => {
              const key = `${detailDiff.previousCategory ?? ""}-${detailDiff.category}-${index}`;
              const oldTrimmed = (detailDiff.oldText ?? "").trim();
              const newTrimmed = (detailDiff.newText ?? "").trim();

              if (oldTrimmed !== "" && newTrimmed === "") {
                return (
                  <DetailToneBlock
                    key={key}
                    tone="removed"
                    category={detailDiff.category}
                    text={detailDiff.oldText ?? ""}
                    chipLabel="Removed from this visit"
                  />
                );
              }
              if (oldTrimmed === "" && newTrimmed !== "") {
                return (
                  <DetailToneBlock
                    key={key}
                    tone="added"
                    category={detailDiff.category}
                    text={detailDiff.newText ?? ""}
                    chipLabel="Added to this visit"
                  />
                );
              }
              return <DetailDiffBlock key={key} diff={detailDiff} />;
            })}
        </div>
      )}
    </div>
  );
};

const SummaryFieldRow: React.FC<{ diff: SummaryFieldDiff }> = ({ diff }) => {
  const label = SUMMARY_FIELD_LABELS[diff.field] ?? diff.field;
  const oldTrimmed = (diff.oldValue ?? "").trim();
  const newTrimmed = (diff.newValue ?? "").trim();
  const isPureRemoval = oldTrimmed !== "" && newTrimmed === "";
  const isPureAddition = oldTrimmed === "" && newTrimmed !== "";

  if (isPureRemoval) {
    return (
      <div className="text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-700">{label}</span>
          <Chip tone="removed" label="Removed" />
        </div>
        <div className="mt-1">
          <TextBlockPanel tone="removed" text={diff.oldValue ?? ""} />
        </div>
      </div>
    );
  }

  if (isPureAddition) {
    return (
      <div className="text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-700">{label}</span>
          <Chip tone="added" label="Added" />
        </div>
        <div className="mt-1">
          <TextBlockPanel tone="added" text={diff.newValue ?? ""} />
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm">
      <span className="font-medium text-neutral-700">{label}:</span>{" "}
      <span className="whitespace-pre-wrap leading-relaxed text-neutral-800">
        <TextSegments segments={diff.segments} />
      </span>
    </div>
  );
};

const ChangeTally: React.FC<{ visitDiffs: VisitDiffEntry[] }> = ({ visitDiffs }) => {
  const counts: Record<VisitDiffStatus, number> = {
    [VisitDiffStatus.Added]: visitDiffs.filter((entry) => entry.status === VisitDiffStatus.Added)
      .length,
    [VisitDiffStatus.Removed]: visitDiffs.filter(
      (entry) => entry.status === VisitDiffStatus.Removed
    ).length,
    [VisitDiffStatus.Modified]: visitDiffs.filter(
      (entry) => entry.status === VisitDiffStatus.Modified
    ).length,
    [VisitDiffStatus.Unchanged]: visitDiffs.filter(
      (entry) => entry.status === VisitDiffStatus.Unchanged
    ).length,
  };
  const visibleStatuses = (
    [VisitDiffStatus.Added, VisitDiffStatus.Removed, VisitDiffStatus.Modified] as const
  ).filter((status) => counts[status] > 0);

  if (visibleStatuses.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs">
      {visibleStatuses.map((status, index) => (
        <React.Fragment key={status}>
          {index > 0 && (
            <span className="text-neutral-300" aria-hidden>
              ·
            </span>
          )}
          <span className={TALLY_CLASSES[status]}>
            {counts[status]} {TALLY_LABELS[status]}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const LoadingState: React.FC = () => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 text-sm font-medium text-neutral-500">
      <div
        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-500"
        aria-hidden
      />
      Loading comparison…
    </div>
    <div className="space-y-2">
      <div className="h-14 animate-pulse rounded-xl bg-neutral-50 ring-1 ring-neutral-100" />
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-16 animate-pulse rounded-xl bg-neutral-50 ring-1 ring-neutral-100"
        />
      ))}
    </div>
  </div>
);

const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <div className="flex min-h-[240px] flex-col items-center justify-center rounded-2xl border border-destructive-border bg-destructive-subtle p-8 text-center">
    <AlertCircle className="mb-3 text-destructive-text" size={24} aria-hidden />
    <h3 className="text-base font-semibold text-destructive-text">Failed to load comparison</h3>
    <p className="mt-1 text-sm text-destructive-text">{error}</p>
  </div>
);

export const VersionsCompareView: React.FC<VersionsCompareViewProps> = ({
  diff,
  isLoading,
  error,
  template,
  fileMapping,
  baseVersion,
  compareVersion,
  onClose,
  section,
}) => {
  const [showUnchanged, setShowUnchanged] = useState(false);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (!diff) return null;

  const visibleVisitDiffs = showUnchanged
    ? diff.visitDiffs
    : diff.visitDiffs.filter((entry) => entry.status !== VisitDiffStatus.Unchanged);
  const changedSummaryDiffs = diff.summaryDiffs.filter((fieldDiff) => fieldDiff.changed);
  const hasChanges =
    section === COMPARE_VERSIONS_SECTIONS.SUMMARY
      ? changedSummaryDiffs.length > 0
      : diff.visitDiffs.some((entry) => entry.status !== VisitDiffStatus.Unchanged);

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 bg-surface-subtle pb-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200/80">
            <GitCompare size={18} aria-hidden />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
              <span className="truncate font-semibold text-neutral-900">
                {baseVersion?.version_name ?? "Base version"}
              </span>
              <ArrowRight size={14} className="shrink-0 text-neutral-300" aria-hidden />
              <span className="truncate font-semibold text-neutral-900">
                {compareVersion?.version_name ?? "Compare version"}
              </span>
            </div>
            {section === COMPARE_VERSIONS_SECTIONS.VISITS && (
              <div className="flex flex-wrap items-center gap-2">
                <ChangeTally visitDiffs={diff.visitDiffs} />
                <Badge shape="tag" variant="outline">
                  {MATCH_MODE_LABELS[diff.matchMode]}
                </Badge>
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          {section === COMPARE_VERSIONS_SECTIONS.VISITS && (
            <LabeledSwitch
              aria-label="Show unchanged visits"
              checked={showUnchanged}
              onCheckedChange={setShowUnchanged}
              label="Show Unchanged"
              variant="blue"
            />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close comparison"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-neutral-500 transition-colors duration-200 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:shadow-focus"
          >
            <X size={18} aria-hidden />
          </button>
        </div>
      </div>

      {!hasChanges && (
        <div className="rounded-xl border border-neutral-200 bg-card p-4 text-sm text-neutral-500 shadow-sm">
          No differences found between these two versions.
        </div>
      )}

      {section === COMPARE_VERSIONS_SECTIONS.SUMMARY && changedSummaryDiffs.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-neutral-900">Case Summary</div>
          <div className="space-y-3 rounded-xl border border-neutral-200 bg-card p-4 shadow-sm">
            {changedSummaryDiffs.map((fieldDiff) => (
              <SummaryFieldRow key={fieldDiff.field} diff={fieldDiff} />
            ))}
          </div>
        </div>
      )}

      {section === COMPARE_VERSIONS_SECTIONS.VISITS && visibleVisitDiffs.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-neutral-900">Visits</div>
          <div className="space-y-2">
            {visibleVisitDiffs.map((entry) => (
              <VisitDiffCard
                key={entry.visitId}
                entry={entry}
                template={template}
                fileMapping={fileMapping}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
